import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";

const LightboxContext = createContext(null);

export const useLightbox = () => useContext(LightboxContext);

export const LightboxProvider = ({ children }) => {
  const [img, setImg] = useState(null);
  const open = useCallback((src, alt) => setImg({ src, alt }), []);
  const close = useCallback(() => setImg(null), []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = img ? "hidden" : "";
    return () => window.removeEventListener("keydown", onKey);
  }, [close, img]);

  return (
    <LightboxContext.Provider value={{ open }}>
      {children}
      <AnimatePresence>
        {img && (
          <motion.div
            data-testid="lightbox-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={close}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-md p-4 sm:p-10"
          >
            <button
              data-testid="lightbox-close"
              onClick={close}
              aria-label="Затвори"
              className="absolute top-4 right-4 sm:top-6 sm:right-6 bg-red-600 hover:bg-red-500 text-white p-2 transition-colors shadow-[0_0_25px_rgba(239,68,68,0.5)]"
            >
              <X size={24} strokeWidth={2.5} />
            </button>
            <motion.img
              key={img.src}
              src={img.src}
              alt={img.alt}
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-full max-h-full object-contain border-2 border-white shadow-2xl"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </LightboxContext.Provider>
  );
};
